/**
 * Formatting helpers mirroring api/internal/domain/format.go.
 *
 * Duplicated across the two languages on purpose: the server needs them for
 * generated rules and server-rendered pages, the client for everything it
 * renders itself. Both are covered by tests so they cannot drift.
 */

const MONTHS_GENITIVE = [
  'января', 'февраля', 'марта', 'апреля', 'мая', 'июня',
  'июля', 'августа', 'сентября', 'октября', 'ноября', 'декабря',
];

const MONTHS_SHORT = [
  'янв', 'фев', 'мар', 'апр', 'май', 'июн',
  'июл', 'авг', 'сен', 'окт', 'ноя', 'дек',
];

const MONTHS_EN = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const MONTHS_EN_SHORT = [
  'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
  'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec',
];

const MONTHS_ES = [
  'enero', 'febrero', 'marzo', 'abril', 'mayo', 'junio',
  'julio', 'agosto', 'septiembre', 'octubre', 'noviembre', 'diciembre',
];

const MONTHS_ES_SHORT = [
  'ene', 'feb', 'mar', 'abr', 'may', 'jun',
  'jul', 'ago', 'sep', 'oct', 'nov', 'dic',
];

/**
 * Язык дат и чисел.
 *
 * Модульная переменная, а не контекст: даты печатают десятки компонентов, и
 * тащить хук в каждый — это переписать их все ради одной строки. Значение
 * ставит провайдер языка при запуске и при переключении, то есть ровно там
 * же, где меняется язык интерфейса.
 */
let locale: 'ru' | 'en' | 'es' = 'ru';

export function setLocale(lang: 'ru' | 'en' | 'es'): void {
  locale = lang;
}

/**
 * Подпись по текущему языку.
 *
 * Для тех нескольких строк, что живут прямо в пакете ui: отдельный словарь
 * ради четырёх подписей не нужен, а зависимость ui от пакета языка — тем
 * более. Испанский без своего варианта берёт английский, а не русский.
 */
export function tr(ru: string, en: string, es?: string): string {
  if (locale === 'es') return es ?? en;
  return locale === 'en' ? en : ru;
}

/** Russian plural: one / few / many. */
export function plural(n: number, one: string, few: string, many: string): string {
  const abs = Math.abs(n);
  const d = abs % 10;
  const h = abs % 100;
  if (d === 1 && h !== 11) return one;
  if (d >= 2 && d <= 4 && (h < 10 || h >= 20)) return few;
  return many;
}

/** Thousands separated by a thin space, as the prototypes' fmt(). */
export function num(n: number): string {
  const sign = n < 0 ? '-' : '';
  const digits = String(Math.abs(Math.trunc(n)));
  let out = '';
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ' ';
    out += digits[i];
  }
  return sign + out;
}

function parse(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(d.getTime())) return null;
  // The Go zero time serialises as year 1; treat it as "unset" rather than
  // rendering "1 января 1 г." on screen.
  if (d.getUTCFullYear() <= 1) return null;
  return d;
}

/** "18 августа 2026 г." */
export function fmtDay(value: string | number | Date | null | undefined): string {
  const d = parse(value);
  if (!d) return '';
  if (locale === 'es') return `${d.getDate()} de ${MONTHS_ES[d.getMonth()]} de ${d.getFullYear()}`;
  if (locale === 'en') return `${MONTHS_EN[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]} ${d.getFullYear()} г.`;
}

/** "25 августа, 19:00" */
export function fmtDateTime(value: string | number | Date | null | undefined): string {
  const d = parse(value);
  if (!d) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  const time = `${pad(d.getHours())}:${pad(d.getMinutes())}`;
  if (locale === 'es') return `${d.getDate()} ${MONTHS_ES_SHORT[d.getMonth()]}, ${time}`;
  if (locale === 'en') return `${MONTHS_EN_SHORT[d.getMonth()]} ${d.getDate()}, ${time}`;
  return `${d.getDate()} ${MONTHS_GENITIVE[d.getMonth()]}, ${time}`;
}

/** "18 авг" */
export function fmtShortDay(value: string | number | Date | null | undefined): string {
  const d = parse(value);
  if (!d) return '';
  if (locale === 'es') return `${d.getDate()} ${MONTHS_ES_SHORT[d.getMonth()]}`;
  if (locale === 'en') return `${MONTHS_EN_SHORT[d.getMonth()]} ${d.getDate()}`;
  return `${d.getDate()} ${MONTHS_SHORT[d.getMonth()]}`;
}

/**
 * Разница в календарных днях: 0 — сегодня, 1 — завтра, отрицательное — в прошлом.
 *
 * Считаем от полуночи до полуночи, а не прошедшими сутками. Иначе розыгрыш
 * завтра в 19:00, если сейчас 10 утра, даёт 1.5 суток и округляется в «через
 * 2 дня» — ровно тогда, когда человеку пришло уведомление «розыгрыш завтра».
 * Math.round по разнице полуночей заодно переживает переход на летнее время,
 * когда в сутках 23 или 25 часов.
 */
export function daysUntil(value: string | number | Date | null | undefined): number | null {
  const d = parse(value);
  if (!d) return null;
  const midnight = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  return Math.round((midnight(d) - midnight(new Date())) / 86_400_000);
}

/** "через 3 дня" / "завтра" / "сегодня" / "4 дня назад" */
export function relativeDay(value: string | number | Date | null | undefined): string {
  const days = daysUntil(value);
  if (days === null) return '';
  if (locale === 'es') {
    if (days === 0) return 'hoy';
    if (days === 1) return 'mañana';
    if (days === -1) return 'ayer';
    if (days > 1) return `en ${days} días`;
    return `hace ${-days} ${-days === 1 ? 'día' : 'días'}`;
  }
  if (locale === 'en') {
    if (days === 0) return 'today';
    if (days === 1) return 'tomorrow';
    if (days === -1) return 'yesterday';
    if (days > 1) return `in ${days} ${days === 1 ? 'day' : 'days'}`;
    return `${-days} ${-days === 1 ? 'day' : 'days'} ago`;
  }
  if (days === 0) return 'сегодня';
  if (days === 1) return 'завтра';
  if (days === -1) return 'вчера';
  if (days > 1) return `через ${days} ${plural(days, 'день', 'дня', 'дней')}`;
  const ago = -days;
  return `${ago} ${plural(ago, 'день', 'дня', 'дней')} назад`;
}

/**
 * A weight as a share of the table total: one decimal below 2%, none above.
 * Matches domain.Percent, including the kept ".0" — these strings also appear
 * in published campaign rules, where the wording must not shift.
 */
export function percent(weight: number, total: number): string {
  const t = total > 0 ? total : 1;
  const p = (100 * weight) / t;
  return p < 2 ? p.toFixed(1) : p.toFixed(0);
}

/**
 * Вероятность выигрыша для одного билета, в процентах.
 *
 * Отдельно от percent(), потому что здесь нужен потолок в 100%. percent()
 * считает долю веса в таблице призов, где сумма долей по построению равна
 * единице. А призов может быть больше, чем билетов, — в начале акции это
 * обычное дело. Без потолка на карточке появляется «шанс одного билета —
 * 359%», что подрывает доверие к странице, которая продаёт проверяемость.
 *
 * Когда призов не меньше, чем билетов, выигрывает каждый: ровно 100%.
 * Пустая строка — считать не из чего.
 */
export function ticketChance(winners: number, tickets: number): string {
  if (winners <= 0 || tickets <= 0) return '';
  if (winners >= tickets) return '100';
  return percent(winners, tickets);
}

export function fmtDate(value: string | number | Date | null | undefined): string {
  return fmtDay(value);
}

/**
 * Сумма в минорных единицах валюты как «1 234,50».
 *
 * Деньги ходят целыми числами и в API, и здесь: 1 сомони = 100 дирамов. Делить
 * на 100 во float ради показа можно, а вот считать нельзя — поэтому целая и
 * дробная часть разбираются отдельно. Повторяет domain.FormatMoney, включая
 * то, что ровные суммы печатаются без копеек.
 */
export function money(minor: number): string {
  const neg = minor < 0;
  const abs = Math.abs(Math.round(minor));
  const major = Math.floor(abs / 100);
  const cents = abs % 100;
  const out = num(major) + (cents > 0 ? ',' + String(cents).padStart(2, '0') : '');
  return neg ? '-' + out : out;
}

/**
 * Разбор введённой суммы в минорные единицы.
 *
 * Принимает «4500», «4 500,50», «4500.5» — человек вводит деньги так, как
 * привык, и ошибкой это быть не должно. Пустая строка и мусор дают 0: поле
 * сметы не обязательно к заполнению.
 */
export function parseMoney(text: string): number {
  const cleaned = text.replace(/[^\d,.-]/g, '').replace(',', '.');
  if (cleaned === '' || cleaned === '-') return 0;
  const value = Number(cleaned);
  if (!Number.isFinite(value)) return 0;
  return Math.round(value * 100);
}

/** Базисные пункты как процент с одним знаком: 3350 → «33,5». Повторяет domain.Percent2. */
export function percentBp(bp: number): string {
  const neg = bp < 0;
  const abs = Math.abs(Math.round(bp));
  const whole = Math.floor(abs / 100);
  const frac = Math.floor((abs % 100) / 10);
  const out = String(whole) + (frac > 0 ? ',' + frac : '');
  return neg ? '-' + out : out;
}

/** Базисные пункты как множитель: 30000 → «3,0×». Для отношения LTV к CAC. */
export function timesBp(bp: number): string {
  const whole = Math.floor(bp / 10000);
  const frac = Math.floor((bp % 10000) / 1000);
  return `${whole},${frac}×`;
}

/**
 * Регионы платформы: код и название без флага-эмодзи.
 *
 * Эмодзи в интерфейсе не используются — они приносят чужую палитру и
 * рендерятся по-разному в каждой ОС. Порядок фиксирован, чтобы списки в
 * приложении и кабинете не расходились.
 */
/**
 * Страны платформы. Порядок и состав совпадают с domain.RegionOrder на
 * сервере — он же и решает, какая страна открыта.
 *
 * Евросоюза и Великобритании здесь нет: GDPR требует согласия до первого
 * счётчика и даёт человеку право потребовать свои данные и их удаление.
 * Открывать страну, где мы не готовы это выполнить, значит обещать то, чего
 * не делаем.
 */
export const REGIONS: Array<{ code: string; name: string }> = [
  { code: 'US', name: 'США' },
  { code: 'TJ', name: 'Таджикистан' },
  { code: 'UZ', name: 'Узбекистан' },
  { code: 'KZ', name: 'Казахстан' },
  { code: 'KG', name: 'Кыргызстан' },
  { code: 'TM', name: 'Туркменистан' },
  { code: 'AZ', name: 'Азербайджан' },
  { code: 'GE', name: 'Грузия' },
  { code: 'AM', name: 'Армения' },
  { code: 'TR', name: 'Турция' },
  { code: 'MN', name: 'Монголия' },
  { code: 'AE', name: 'ОАЭ' },
  { code: 'SA', name: 'Саудовская Аравия' },
  { code: 'QA', name: 'Катар' },
  { code: 'EG', name: 'Египет' },
  { code: 'JP', name: 'Япония' },
  { code: 'KR', name: 'Южная Корея' },
  { code: 'SG', name: 'Сингапур' },
];

/** Название региона по коду; неизвестный код возвращается как есть. */
export function regionName(code: string): string {
  return REGIONS.find((r) => r.code === code)?.name ?? code;
}
